if (!customElements.get('sj-cart-recommendation')) {
  const seen = new Set();
  let privacyLoading = false;

  customElements.define('sj-cart-recommendation', class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.button = this.querySelector('[data-sj-cart-recommendation-add]');
      this.label = this.querySelector('[data-sj-cart-recommendation-label]');
      this.error = this.querySelector('[data-sj-cart-recommendation-error]');
      this.onAdd = () => this.addItem();
      this.onView = () => this.checkView();
      this.button?.addEventListener('click', this.onAdd);
      document.addEventListener('visitorConsentCollected', this.onView);
      document.addEventListener('visibilitychange', this.onView);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5);
        this.checkView();
      }, { threshold: [0, 0.5] });
      this.observer.observe(this);
      this.drawerObserver = new MutationObserver(this.onView);
      this.drawerObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures && !privacyLoading) {
        privacyLoading = true;
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
          privacyLoading = false;
          document.querySelectorAll('sj-cart-recommendation').forEach(card => card.checkView());
        });
      }
    }

    disconnectedCallback() {
      this.connected = false;
      clearTimeout(this.viewTimer);
      this.observer?.disconnect();
      this.drawerObserver?.disconnect();
      this.button?.removeEventListener('click', this.onAdd);
      document.removeEventListener('visitorConsentCollected', this.onView);
      document.removeEventListener('visibilitychange', this.onView);
    }

    analyticsAllowed() {
      try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
      catch (_) { return false; }
    }

    visible() {
      const activeSurface = this.dataset.surface === 'cart_page'
        || document.documentElement.classList.contains('open-minicart');
      return this.isConnected && this.inView && document.visibilityState === 'visible' && activeSurface;
    }

    checkView() {
      clearTimeout(this.viewTimer);
      const key = `${this.dataset.sourceProductId}:${this.dataset.productId}`;
      if (!this.visible() || !this.analyticsAllowed() || seen.has(key)) return;
      this.viewTimer = setTimeout(() => {
        if (!this.visible() || !this.analyticsAllowed() || seen.has(key)) return;
        if (this.emit('viewed')) seen.add(key);
      }, 500);
    }

    emit(action) {
      if (!this.analyticsAllowed()) return false;
      const payload = {
        event: `sj_cart_recommendation_${action}`,
        source_product_id: this.dataset.sourceProductId,
        product_id: this.dataset.productId,
        variant_id: this.dataset.variantId,
        product_kind: this.dataset.productKind,
        value: Number(this.dataset.price),
        currency: this.dataset.currency,
        campaign: this.dataset.campaign,
      };
      let emitted = false;
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        emitted = true;
      } catch (_) { /* Analytics cannot block cart actions. */ }
      try {
        const published = window.Shopify?.analytics?.publish(`sj:cart_recommendation_${action}`, payload);
        if (published && typeof published.catch === 'function') published.catch(() => {});
      } catch (_) { /* A pixel subscriber is optional. */ }
      return emitted;
    }

    async addItem() {
      const variantId = Number(this.dataset.variantId);
      if (!variantId || !this.button || this.busy || this.added) return;
      this.busy = true;
      this.button.disabled = true;
      this.error.hidden = true;
      this.label.textContent = this.button.dataset.labelAdding;
      const root = window.Shopify?.routes?.root || '/';
      let posted = false;
      let rejected = false;
      try {
        // A refreshed cart prevents duplicate adds from stale drawer markup.
        const currentResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!currentResponse.ok) throw new Error('Cart unavailable');
        const current = await currentResponse.json();
        if (current.items.some(item => String(item.product_id) === this.dataset.productId)) {
          window.location.assign(`${root}cart`);
          return;
        }
        // Functional cart state: an accepted suggestion must not spawn another upsell.
        const item = { id: variantId, quantity: 1, properties: { _sj_cart_role: 'recommendation' } };
        if (this.analyticsAllowed()) {
          Object.assign(item.properties, {
            _sj_recommendation: this.dataset.campaign,
            _sj_source_product: this.dataset.sourceProductId,
          });
        }
        posted = true;
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [item], sections: ['minicart-form'], sections_url: window.location.pathname }),
        });
        rejected = !response.ok;
        const result = await response.json();
        if (!response.ok || result.status || result.errors) {
          rejected = true;
          throw new Error('Cart update rejected');
        }
        this.added = true;
        this.label.textContent = this.button.dataset.labelAdded;
        this.emit('added');
        // Once added, a rendering failure must never enable a second submission.
        const markup = result.sections?.['minicart-form'];
        const target = document.getElementById('minicart-form');
        const fresh = markup && new DOMParser().parseFromString(markup, 'text/html').querySelector('#minicart-form');
        if (!target || !fresh) {
          window.location.assign(`${root}cart`);
          return;
        }
        target.innerHTML = fresh.innerHTML;
        const drawer = document.querySelector('cart-notification');
        if (typeof drawer?.cartAction === 'function') drawer.cartAction();
        this.updateCartIndicators(root);
      } catch (_) {
        if (this.added || (posted && !rejected)) {
          // The server may have accepted the add before the network failed.
          window.location.assign(`${root}cart`);
          return;
        }
        this.busy = false;
        this.button.disabled = false;
        this.label.textContent = this.button.dataset.labelDefault;
        this.error.hidden = false;
      }
    }

    async updateCartIndicators(root) {
      try {
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) return;
        const cart = await response.json();
        document.querySelectorAll('.cart-count').forEach(count => {
          count.textContent = count.classList.contains('cart-count-drawer') ? `(${cart.item_count})` : cart.item_count > 100 ? '~' : cart.item_count;
        });
        const total = document.querySelector('header-total-price');
        if (typeof total?.updateTotal === 'function') total.updateTotal(cart);
        const shipping = document.querySelector('free-ship-progress-bar');
        if (typeof shipping?.init === 'function') shipping.init(cart.items_subtotal_price);
      } catch (_) { /* The server-rendered cart is already updated. */ }
    }
  });
}
